import getSubdomain from 'utils/getSubdomain';

const STORAGE_KEY = 'ommnio:activeCompanyId';

let activeCompanyId = null;
let tokenLastCompanyId = null;

export const getActiveCompanyId = () => {
  if (activeCompanyId) {
    return activeCompanyId;
  }
  try {
    return sessionStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

export const setActiveCompanyId = companyId => {
  activeCompanyId = companyId || null;
  try {
    if (companyId) {
      sessionStorage.setItem(STORAGE_KEY, companyId);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // sessionStorage unavailable (SSR/tests)
  }
};

export const clearActiveCompanyId = () => {
  setActiveCompanyId(null);
};

/** Server-side session last company (Token.lastCompany); set from /v2/user/me. */
export const setTokenLastCompanyId = companyId => {
  tokenLastCompanyId = companyId || null;
};

export const getTokenLastCompanyId = () => tokenLastCompanyId;

export const clearTokenLastCompanyId = () => {
  tokenLastCompanyId = null;
};

/** Apex/preview: session cache (switcher) → token last company → persisted auth.company_id. */
export const resolvePreferredCompanyId = (serverLastCompanyId, persistedCompanyId) =>
  getActiveCompanyId() || serverLastCompanyId || persistedCompanyId || null;

export const resolveCompanyFromSubdomain = (companies, subdomain) => {
  if (!subdomain || !companies?.length) {
    return null;
  }
  const list = Array.isArray(companies) ? companies : Object.values(companies);
  return list.find(c => c.subDomain === subdomain) || null;
};

/** Merge persisted entity companies with auth user.companies (membership is authoritative on user). */
export const mergeCompanyLists = (persistedCompanies, userCompanies) => {
  const fromReducer = Array.isArray(persistedCompanies)
    ? persistedCompanies
    : Object.values(persistedCompanies || {});
  const fromUser = userCompanies || [];
  if (fromReducer.length === 0) {
    return fromUser;
  }
  if (fromUser.length === 0) {
    return fromReducer;
  }
  const byId = new Map(fromReducer.map(c => [c._id, c]));
  for (const company of fromUser) {
    if (!byId.has(company._id)) {
      byId.set(company._id, company);
    }
  }
  return Array.from(byId.values());
};

/** Pick active company from URL subdomain; returns company _id or null. */
export const syncActiveCompanyFromUrl = companies => {
  const subdomain = getSubdomain();
  const match = resolveCompanyFromSubdomain(companies, subdomain);
  if (match?._id) {
    setActiveCompanyId(match._id);
    return match._id;
  }
  return null;
};

/**
 * True when persisted auth.company_id still lags behind the URL subdomain after a company hop.
 * AuthGate/HomeGate hold loading until this is false so child routes do not mount on stale tenant
 * state. Scoped to subdomain hosts only: GET_USER_DATA_FULFILLED resolves company_id from the URL
 * subdomain, so this is guaranteed to converge after /me. Apex/preview hosts are intentionally
 * excluded — they never reconcile company_id to the session cache (which would loop), and HomeGate
 * already returns null + redirects to a company subdomain before rendering protected content there.
 */
export const needsCompanyRefetch = ({
  subdomain = getSubdomain(),
  urlCompanyId,
  storedCompanyId,
} = {}) =>
  Boolean(subdomain && urlCompanyId && String(urlCompanyId) !== String(storedCompanyId || ''));

export const pickDefaultCompany = (user, preferredCompanyId) => {
  if (!user?.companies?.length) {
    return null;
  }
  const urlSubdomain = getSubdomain();
  const fromUrl = resolveCompanyFromSubdomain(user.companies, urlSubdomain);
  if (fromUrl) {
    return fromUrl;
  }
  if (preferredCompanyId) {
    const fromToken = user.companies.find(c => c._id === preferredCompanyId);
    if (fromToken) {
      return fromToken;
    }
  }
  const sorted = [...user.companies].sort(
    (a, b) => (b.employee?.lastSeen || 0) - (a.employee?.lastSeen || 0),
  );
  return sorted[0] || user.companies[0];
};
